import useGetNavigation from "@RHCommerceDev/hooks/useGetNavigation";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import {
  countries,
  countryAbbreviationMapper
} from "@RHCommerceDev/resources/countries-config.json";
import { cn } from "@RHCommerceDev/utils/cn";
import parsedCatalogNavigation, {
  groupChildCategories
} from "@RHCommerceDev/utils/parseCatalogNavigationData";
import { usePageContent } from "customProviders/LocationProvider";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react";
import { Typography } from "unstyled-ui-components";
import HamburgerCountrySelector from "./hamburgerCountrySelector";
import memoize from "@RHCommerceDev/utils/memoize";
import getLocalizedUrl from "@RHCommerceDev/utils/getLocalizedUrl";
import useScrollLock from "@RHCommerceDev/hooks/useScrollLock";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import { useAppId } from "@RHCommerceDev/hooks/useAppId/useAppId";
import he from "he";
import { AnimatePresence, motion } from "motion/react";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { usePreloadImages } from "@RHCommerceDev/hooks/usePreloadImages";

const handleLinkClick = (
  e: React.SyntheticEvent,
  navElement: NavigationElement,
  locale: string,
  isConcierge: boolean
) => {
  e.preventDefault?.();
  const { targetUrl, linkType } = navElement;
  const url = getLocalizedUrl(targetUrl ?? "", locale, isConcierge);

  if (typeof window === "undefined") return;

  if (linkType === "none" && targetUrl) {
    window.open(url, "_blank");
    return;
  }

  if (linkType === "anchor") {
    const formattedUrl = targetUrl?.startsWith("https://")
      ? targetUrl
      : `https://${targetUrl}`;
    window.open(formattedUrl, "_blank");
    return;
  }

  if (targetUrl?.trim().indexOf("http") === 0) {
    window.location.assign(targetUrl);
  } else {
    window.location.href = url;
  }
};

export const PrimaryColumn = ({
  sections,
  selectedItem,
  handlePrimaryClick,
  locale,
  selectedCountry,
  selectedLanguage,
  isCountrySelectorOpen,
  setIsCountrySelectorOpen,
  isConcierge,
  hamburgerMobileDrawer
}: {
  sections: (NavigationElement | null | undefined)[];
  selectedItem: {
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  };
  handlePrimaryClick: (sectionIndex: number, primaryIndex: number) => void;
  locale: string;
  selectedCountry: string;
  selectedLanguage: string;
  isCountrySelectorOpen: boolean;
  setIsCountrySelectorOpen: Dispatch<SetStateAction<boolean>>;
  isConcierge: boolean;
  hamburgerMobileDrawer?: boolean;
}) => {
  return (
    <div
      className="w-3xs flex min-w-[202px] flex-col items-start gap-12 overflow-auto [-ms-overflow-style:none] [scrollbar-width:none] md:!w-[202px]
      md:border-r md:border-[#EBE7DF]/35 lg:!w-[247px] [&::-webkit-scrollbar]:hidden"
    >
      {sections?.map((section, sectionIndex) => (
        <div
          key={sectionIndex}
          className="w-full md:max-w-[162px] lg:max-w-[167px]"
        >
          <Typography
            variant="default"
            fontFamily="rhc"
            fontWeight="semibold"
            className="break-inside-void mb-5 text-left text-base uppercase leading-[1.5em] tracking-[0.05em] sm:mb-6 md:mb-4 md:text-[13px]  lg:mb-5 lg:text-base"
            dangerouslySetInnerHTML={{
              __html: he.decode(section?.displayName ?? "")
            }}
          />

          {section?.childCategories?.map((primaryItem, primaryIndex) => (
            <button
              key={primaryIndex}
              data-analytics-rootcat={section?.displayName}
              onClick={e => {
                if (!!primaryItem?.childCategories?.length) {
                  setIsCountrySelectorOpen(false);
                  handlePrimaryClick(sectionIndex, primaryIndex);
                } else {
                  handleLinkClick(e, primaryItem, locale, isConcierge);
                  const displayName = primaryItem?.displayName;
                  const target = e?.target as HTMLElement;
                  const { id, className } = target;

                  document?.body?.dispatchEvent(
                    new CustomEvent("cta_click", {
                      detail: {
                        item: {
                          fromHamburger: !hamburgerMobileDrawer && true,
                          isHeaderNavigation: true,
                          topCat:
                            sectionIndex > 3
                              ? "footer"
                              : sectionIndex === 0
                              ? displayName
                              : section?.displayName,
                          parentCat:
                            sectionIndex > 3
                              ? section?.displayName
                              : displayName?.replaceAll("RH", "").trim(),
                          cat:
                            sectionIndex > 3
                              ? displayName
                              : primaryItem?.targetUrl,
                          catURL: primaryItem?.targetUrl,
                          class: className,
                          id: id
                        }
                      }
                    })
                  );
                }
              }}
              className="break-inside-void mb-5 block p-0 text-white  sm:mb-6 md:mb-4 lg:mb-5"
            >
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-left text-base uppercase leading-[1.5em] tracking-[0.05em] text-inherit md:text-[13px] lg:text-base",
                  {
                    "font-semibold":
                      selectedItem?.primaryIndex === primaryIndex &&
                      selectedItem?.sectionIndex === sectionIndex
                  }
                )}
                dangerouslySetInnerHTML={{
                  __html: he.decode(primaryItem?.displayName ?? "")
                }}
              />
            </button>
          ))}

          {sectionIndex === 0 && (
            <button
              onClick={() => {
                setIsCountrySelectorOpen(prev => !prev);
                handlePrimaryClick(-1, -1);
              }}
              className="relative mt-7 flex w-full items-center  p-0 py-4 uppercase text-white before:absolute before:top-0 before:h-px before:w-full before:bg-white before:opacity-25 before:content-[''] after:absolute after:bottom-0 after:h-px after:w-full after:bg-white after:opacity-25 after:content-[''] sm:mt-6 md:mt-8 lg:mt-7"
            >
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-base uppercase leading-[3em] tracking-[0.05em] text-inherit md:text-[13px] lg:text-base ",
                  {
                    "font-semibold": isCountrySelectorOpen
                  }
                )}
              >
                {
                  countryAbbreviationMapper[
                    selectedCountry === "MC"
                      ? "FR"
                      : (selectedCountry as keyof typeof countryAbbreviationMapper)
                  ]
                }
                {countries[
                  selectedCountry === "MC"
                    ? "FR"
                    : (selectedCountry as keyof typeof countries)
                ].label?.match(/^(.+?)\s*(\([^)]+\))?$/)?.[2] ?? ""}{" "}
                {selectedLanguage}
              </Typography>
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export const SecondaryColumn = ({
  items,
  selectedItem,
  handleSecondaryClick,
  locale,
  isCountrySelectorOpen,
  isConcierge,
  primaryCategory,
  hamburgerMobileDrawer,
  waterWorksUrl
}: {
  items: (NavigationElement | null | undefined)[] | undefined;
  selectedItem: {
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  };
  handleSecondaryClick: (index: number) => void;
  locale: string;
  isCountrySelectorOpen: boolean;
  isConcierge: boolean;
  primaryCategory?: string;
  hamburgerMobileDrawer?: boolean;
  waterWorksUrl?: string;
}) => (
  <div className="mt-5  min-w-[230px] overflow-auto pr-3 text-white [-ms-overflow-style:none] [scrollbar-width:none] md:mt-10 [&::-webkit-scrollbar]:hidden">
    {isCountrySelectorOpen ? (
      <HamburgerCountrySelector />
    ) : (
      <>
        {items?.map((secondaryItem, secondaryIndex) => (
          <button
            data-analytics-parentCat={secondaryItem?.displayName}
            key={secondaryIndex}
            onClick={e => {
              if (!!secondaryItem?.childCategories?.length) {
                handleSecondaryClick(secondaryIndex);
              } else {
                if (!secondaryItem) return;
                handleLinkClick(e, secondaryItem, locale, isConcierge);
                const displayName = secondaryItem?.displayName;
                const target = e?.target as HTMLElement;
                const { id, className } = target;
                document?.body?.dispatchEvent(
                  new CustomEvent("cta_click", {
                    detail: {
                      item: {
                        fromHamburger: !hamburgerMobileDrawer && true,
                        isHeaderNavigation: true,
                        topCat: primaryCategory,
                        parentCat: displayName,
                        cat: secondaryItem?.targetUrl,
                        catURL: secondaryItem?.targetUrl,
                        class: className,
                        id: id
                      }
                    }
                  })
                );
              }
            }}
            className="mb-6 block p-0 text-white md:mb-4 lg:mb-5"
          >
            {secondaryItem?.displayName?.includes("ww-logo") ? (
              <img
                src={waterWorksUrl}
                alt={secondaryItem?.displayName ?? ""}
                width={188}
                height={16}
                className="h-4 w-[188px]"
              />
            ) : (
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                className={cn(
                  "text-left text-base uppercase leading-[1.5em] tracking-[0.05em] text-inherit md:text-[13px] lg:text-base",
                  {
                    "font-semibold":
                      selectedItem.secondaryIndex === secondaryIndex
                  }
                )}
                dangerouslySetInnerHTML={{
                  __html: he.decode(secondaryItem?.displayName ?? "")
                }}
              />
            )}
          </button>
        ))}
      </>
    )}
  </div>
);

export const TertiaryColumn = ({
  groups,
  locale,
  isConcierge,
  primaryCategory,
  secondaryCategory,
  hamburgerMobileDrawer,
  waterWorksUrl
}: {
  groups: ReturnType<typeof groupChildCategories>;
  locale: string;
  isConcierge: boolean;
  primaryCategory?: string;
  secondaryCategory?: string;
  hamburgerMobileDrawer?: boolean;
  waterWorksUrl?: string;
}) => (
  <div
    className="mt-5 columns-2 gap-x-[75px] text-white [-ms-overflow-style:none] [column-fill:_auto] [scrollbar-width:none] sm:mt-4 md:mt-10 md:overflow-x-auto lg:gap-x-20
"
  >
    {groups?.map((group, groundIndex) => (
      <div
        key={groundIndex}
        className="mb-[30px] w-full break-inside-avoid md:mb-4 lg:mb-5"
      >
        {group?.header &&
          (group?.header?.displayName?.includes("ww-logo") ? (
            <img
              src={waterWorksUrl}
              alt={group?.header?.displayName ?? ""}
              width={188}
              height={16}
              className="mb-4 h-4 w-[188px] md:mb-2"
            />
          ) : (
            <Typography
              variant="default"
              fontFamily="rhc"
              fontWeight="semibold"
              className="mb-4 block text-left text-base uppercase leading-[1.5em] tracking-[0.05em] text-white md:mb-2 md:text-[13px] lg:text-base"
              dangerouslySetInnerHTML={{
                __html: he.decode(group.header?.displayName ?? "")
              }}
            />
          ))}

        {group?.children?.map((tertiaryItem, tertiaryIndex) => (
          <button
            key={tertiaryIndex}
            className="mb-4 block p-0 text-white md:mb-2"
            onClick={e => {
              handleLinkClick(e, tertiaryItem, locale, isConcierge);
              const target = e?.target as HTMLElement;
              const { id, className } = target;
              document?.body?.dispatchEvent(
                new CustomEvent("cta_click", {
                  detail: {
                    item: {
                      fromHamburger: !hamburgerMobileDrawer && true,
                      isHeaderNavigation: true,
                      topCat: primaryCategory,
                      parentCat: secondaryCategory,
                      cat: group?.header
                        ? group?.header?.displayName
                        : tertiaryItem?.displayName,
                      catURL: group?.header
                        ? tertiaryItem?.displayName
                        : tertiaryItem?.targetUrl,
                      class: className,
                      id: id
                    }
                  }
                })
              );
            }}
          >
            {tertiaryItem?.displayName?.includes("ww-logo") ? (
              <img
                src={waterWorksUrl}
                alt={tertiaryItem?.displayName ?? ""}
                width={188}
                height={16}
                className="h-4 w-[188px] brightness-0 invert"
              />
            ) : (
              <Typography
                variant="default"
                fontFamily="rhc"
                fontWeight="thin"
                className="w-max text-left text-base capitalize leading-[1.5em] tracking-[0.05em] text-white md:text-[13px] lg:text-base"
                dangerouslySetInnerHTML={{
                  __html: he.decode(tertiaryItem?.displayName ?? "")
                }}
              />
            )}
          </button>
        ))}
      </div>
    ))}
  </div>
);

const WORHMenuContentV2 = () => {
  const locale = useLocalization();
  const env = useEnv();
  const { isConcierge } = useAppId();
  const { pageContent: pageContentObj } = usePageContent();
  const hamburgerNav = pageContentObj?.hamburgerNav as Record<string, unknown>;
  const pageContent = hamburgerNav?.childCategories
    ? Array.isArray(hamburgerNav.childCategories)
      ? hamburgerNav.childCategories
      : Object.values(hamburgerNav.childCategories)
    : [];

  const { catalogNavigation, saleCatalogNavigation } = useGetNavigation();
  const navigationData = parsedCatalogNavigation(
    catalogNavigation,
    saleCatalogNavigation,
    isConcierge
  );

  const { country, language } = useUserPreferences();

  const sections = [navigationData];
  sections.push(...pageContent);

  const [selectedItem, setSelectedItem] = useState<{
    sectionIndex: number;
    primaryIndex: number;
    secondaryIndex: number;
  }>({
    sectionIndex: -1,
    primaryIndex: -1,
    secondaryIndex: -1
  });

  const [isCountrySelectorOpen, setIsCountrySelectorOpen] =
    useState<boolean>(false);

  const selectedPrimary =
    sections?.[selectedItem.sectionIndex]?.childCategories?.[
      selectedItem.primaryIndex
    ];
  const selectedSecondary =
    selectedPrimary?.childCategories?.[selectedItem.secondaryIndex];

  const groupedCategories = groupChildCategories(
    selectedSecondary?.childCategories
  );

  const handlePrimaryClick = useCallback(
    (sectionIndex: number, primaryIndex: number) => {
      setSelectedItem({ sectionIndex, primaryIndex, secondaryIndex: -1 });
    },
    []
  );

  const handleSecondaryClick = useCallback((secondaryIndex: number) => {
    setSelectedItem(prev => ({ ...prev, secondaryIndex }));
  }, []);

  const { state } = useHeaderAtom();

  const isHamburgerDropdownOpen = state.isHamburgerDropdownOpen;

  const dropdownHeight = state.headerRect
    ? `calc(100dvh - ${state.headerRect?.height}px)`
    : undefined;

  const imgPublicUrl = (env.PUBLIC_URL || "").replace(new RegExp(/\/?$/gm), "");

  // Preload Waterworks logo image
  usePreloadImages(["/img/WaterworksLogoWhite.svg"]);

  //Resets selected state on every render
  useEffect(() => {
    if (!isHamburgerDropdownOpen) {
      setSelectedItem({
        sectionIndex: -1,
        primaryIndex: -1,
        secondaryIndex: -1
      });
    }
  }, [isHamburgerDropdownOpen]);

  //lock screen when hamburger desktop is open
  useScrollLock(state.isHamburgerDropdownOpen);

  return (
    <>
      <AnimatePresence mode="wait">
        {isHamburgerDropdownOpen && (
          <motion.div
            key="dropdown"
            initial={{ y: "-100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "-100%", opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-0 z-[1099] flex h-screen w-screen flex-col justify-end bg-rh-mocha text-white"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: { delay: 0.2, duration: 0.4 }
              }}
              exit={{ opacity: 0, transition: { delay: 0, duration: 0.1 } }}
              className=" flex gap-[75px] px-10 lg:gap-20 xl:px-20"
              style={{
                height: dropdownHeight
              }}
            >
              {/* PRIMARY COLUMN */}

              <PrimaryColumn
                sections={sections}
                selectedItem={selectedItem}
                handlePrimaryClick={handlePrimaryClick}
                locale={locale}
                selectedCountry={country}
                selectedLanguage={language.split("-")[0].toUpperCase()}
                isCountrySelectorOpen={isCountrySelectorOpen}
                setIsCountrySelectorOpen={setIsCountrySelectorOpen}
                isConcierge={isConcierge}
              />

              {/* SECONDARY COLUMN */}
              {(selectedItem.primaryIndex !== -1 || isCountrySelectorOpen) && (
                <SecondaryColumn
                  items={selectedPrimary?.childCategories ?? undefined}
                  selectedItem={selectedItem}
                  handleSecondaryClick={handleSecondaryClick}
                  locale={locale}
                  isCountrySelectorOpen={isCountrySelectorOpen}
                  isConcierge={isConcierge}
                  primaryCategory={
                    sections[selectedItem.sectionIndex]?.childCategories?.[
                      selectedItem.primaryIndex
                    ]?.displayName || ""
                  }
                  waterWorksUrl={`${imgPublicUrl}/img/WaterworksLogoWhite.svg`}
                />
              )}

              {/* TERTIARY COLUMN */}
              {selectedItem.secondaryIndex !== -1 && (
                <TertiaryColumn
                  groups={groupedCategories}
                  locale={locale}
                  isConcierge={isConcierge}
                  primaryCategory={
                    sections[selectedItem.sectionIndex]?.childCategories?.[
                      selectedItem.primaryIndex
                    ]?.displayName || ""
                  }
                  secondaryCategory={
                    selectedPrimary?.childCategories?.[
                      selectedItem.secondaryIndex
                    ]?.displayName || ""
                  }
                  waterWorksUrl={`${imgPublicUrl}/img/WaterworksLogoWhite.svg`}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default memoize(WORHMenuContentV2);
